import { createRoot } from "react-dom/client";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@radix-ui/themes/styles.css";
import "@workos-inc/widgets/styles.css";
import "leaflet/dist/leaflet.css";
import "./index.css";
import App from "./App.tsx";
import { clearAuthKitState } from "@/shared/auth/authRecovery";
import { env } from "./shared/config/env";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";

// Get the current origin for the redirect URI
const redirectUri = `${window.location.origin}/callback`;
const host = window.location.hostname;
const configuredDevMode = env.workos.devMode;
const isLocalHost =
  host === "localhost" ||
  host === "127.0.0.1" ||
  host === "::1" ||
  host.startsWith("local.");
const authkitDevMode = configuredDevMode || isLocalHost;

console.log("[main.tsx] Redirect URI:", redirectUri);
console.log("[main.tsx] AuthKit devMode:", authkitDevMode);

/**
 * AuthKit SDK expects callback `state` to be JSON when present.
 * If an opaque/non-JSON state leaks into the callback URL, initialization can fail
 * and leave auth in a stuck loading state. Sanitize before provider bootstrap.
 */
if (window.location.pathname === "/callback") {
  const url = new URL(window.location.href);
  const stateParam = url.searchParams.get("state");

  if (stateParam) {
    try {
      JSON.parse(stateParam);
    } catch {
      url.searchParams.delete("state");
      window.history.replaceState({}, "", url);
      clearAuthKitState({ preserveCodeVerifier: true });
      console.warn(
        "[main.tsx] Removed invalid callback state and cleared auth state for recovery (preserving code verifier)"
      );
    }
  }
}

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary variant="bare">
  <AuthKitProvider
    clientId={env.workos.clientId ?? ""}
    apiHostname={env.workos.apiHostname}
    redirectUri={redirectUri}
    devMode={authkitDevMode}
    onRefreshFailure={({ signIn }) => {
      console.warn("[AuthKitProvider] Session refresh failed, redirecting to sign-in");
      signIn();
    }}
  >
    <App />
  </AuthKitProvider>
  </ErrorBoundary>
);
